import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Run } from '@orqforge/shared';
import { apiClient, type ApDemoCase, type ApDemoOverview, type ApDemoProcessStep } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';

function relativeTime(value?: string | Date): string {
  if (!value) return '-';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '-';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function ProcessMap({ steps, activeKey }: { steps: ApDemoProcessStep[]; activeKey: string | null }) {
  return (
    <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6" data-testid="ap-process-map">
      {steps.map((step, i) => (
        <li
          key={step.key}
          data-testid={`ap-step-${step.key}`}
          className={`rounded-lg border px-3 py-2 ${activeKey === step.key ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/40' : 'border-gray-200 dark:border-gray-700'}`}
        >
          <div className="text-[0.65rem] uppercase tracking-wide text-gray-400">
            {i + 1} · {step.actor}
          </div>
          <div className="text-sm font-medium">{step.label}</div>
          <div className="text-[0.7rem] text-gray-500">{step.human ? 'Human gate' : 'Agent'}</div>
        </li>
      ))}
    </ol>
  );
}

export function DashboardPage() {
  const { workspaces, workspaceId } = useApp();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const [overview, setOverview] = useState<ApDemoOverview | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);

  useEffect(() => {
    void apiClient.getApDemoOverview().then(setOverview).catch(() => setOverview(null));
    void apiClient.listRuns().then(setRuns).catch(() => setRuns([]));
  }, [workspaceId]);

  const cases = overview?.cases ?? [];
  const needsYou = cases.filter((c) => c.status === 'AWAITING_APPROVAL' || c.status === 'EXCEPTION' || c.status === 'ON_HOLD');
  const payableCount = cases.filter((c) => c.status === 'IN_PAYMENT_PROPOSAL').length;
  const highlight =
    needsYou[0]?.phase ??
    cases.find((c) => c.status === 'IN_PAYMENT_PROPOSAL')?.phase ??
    cases[0]?.phase ??
    'receive';

  return (
    <div>
      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
        <div className="text-[0.7rem] uppercase tracking-[0.16em] text-gray-400">{workspace?.name || 'No workspace selected'}</div>
        <h1 className="mt-2 text-3xl font-bold">Operations</h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-500">
          Invoice-to-pay flow for finance. Invoices arrive in Mailbox. Exceptions wait on people. Payment orders wait for a human Pay click. Chat is for inspection, not for starting invoices.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/mailbox" data-testid="dash-mailbox"><Button size="sm">Mailbox</Button></Link>
          <Link to="/payments" data-testid="dash-payments"><Button size="sm" variant="outline">Payment orders</Button></Link>
          <Link to="/runs" data-testid="dash-runs"><Button size="sm" variant="ghost">Runs</Button></Link>
          <Link to="/collaborate" data-testid="dash-collaborate"><Button size="sm" variant="ghost">Ask about this process</Button></Link>
        </div>
        <p className="mt-3 text-xs text-gray-400">Illustrative Hisense Europe CFSSC mock when the AP pack is enabled. No live SAP or bank.</p>
      </section>
      <section className="mt-5 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <h2 className="mb-3 text-sm font-semibold">Process flow</h2>
        <ProcessMap steps={overview?.processSteps ?? []} activeKey={highlight} />
      </section>
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 lg:col-span-2">
          <header className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5 dark:border-gray-800">
            <h2 className="text-sm font-semibold">Now running</h2>
            <Link to="/mailbox" className="text-xs text-gray-500">Open mailbox</Link>
          </header>
          <div className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="dash-running">
            {cases.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">
                No AP cases yet. <Link to="/mailbox" className="text-blue-600">Drop an invoice</Link>
              </div>
            ) : cases.map((c: ApDemoCase) => (
              <Link
                key={c.case_id}
                to={c.payment_line_id ? `/payments?caseId=${c.case_id}` : '/mailbox'}
                className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800"
                data-testid={`dash-case-${c.case_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{c.invoice_number} · {c.vendor_name}</div>
                  <div className="text-xs text-gray-500">{c.phase_label} · {c.gross_total} {c.currency}</div>
                </div>
                <Badge>{c.status}</Badge>
              </Link>
            ))}
          </div>
        </section>
        <section className="rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="mb-3 text-sm font-semibold">Needs you</h2>
          <div className="flex flex-col gap-2" data-testid="dash-needs-you">
            {needsYou.length === 0 ? <p className="text-sm text-gray-500">No human gates waiting.</p> : needsYou.map((c) => (
              <Link key={c.case_id} to="/mailbox" className="rounded-lg bg-gray-50 px-3 py-2.5 text-sm dark:bg-gray-800" data-testid={`dash-need-${c.case_id}`}>
                <div className="font-medium">{c.invoice_number}</div>
                <div className="text-xs text-gray-500">{c.approver_role || 'ap_clerk'} · {c.reason_codes.join(', ') || c.status}</div>
              </Link>
            ))}
            <Link to="/payments" className="mt-2 flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5 text-sm dark:bg-gray-800" data-testid="dash-treasury">
              <span>Treasury Pay</span>
              <Badge variant={payableCount ? 'success' : 'secondary'}>{payableCount}</Badge>
            </Link>
          </div>
        </section>
      </div>
      <section className="mt-5 rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5 dark:border-gray-800">
          <h2 className="text-sm font-semibold">Recent agent runs</h2>
          <Link to="/runs" className="text-xs text-gray-500">View all</Link>
        </header>
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {runs.slice(0, 6).length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No agent runs in this workspace.</div>
          ) : runs.slice(0, 6).map((r) => (
            <Link key={r.id} to={`/runs?runId=${r.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{r.input || `run-${r.id.slice(0, 8)}`}</div>
                <div className="text-xs text-gray-500">{r.currentStage || '-'} · {relativeTime(r.createdAt)}</div>
              </div>
              <Badge>{r.status}</Badge>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
